import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  TextField,
  Typography,
  Button,
  CircularProgress,
  Avatar,
  Divider,
  MenuItem,
  FormHelperText,
} from "@mui/material";
import InputAdornment from "@mui/material/InputAdornment";

import CalendarTodayIcon from "@mui/icons-material/CalendarToday";

import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider, DateTimePicker } from "@mui/x-date-pickers";
import { debounce } from "lodash";
import { fetchPkDetail, searchUser } from "../../DAL/fetch";
import { setPk, updatePk } from "../../DAL/update";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";
import { enqueueSnackbar } from "notistack";
import { fileUrl } from "../../Config/Config";

const SetPk = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const location = useLocation();
  const type = location.pathname.includes("/setpk") ? "Schedule" : "Update";
  const gradientStyle =
    "linear-gradient(to right, #8832d1, #11c8d8, #11c8d8, #11c8d8, #11c8d8)";
  const [data1, setData1] = useState(null); // Data for the first user
  const [data2, setData2] = useState(null); // Data for the second user
  const [searchLoading1, setSearchLoading1] = useState(false);
  const [searchLoading2, setSearchLoading2] = useState(false);
  const [message, setMessage] = useState(false);
  const [pkName, setPkName] = useState("");
  const [selectedDateAndTime, setSelectedDateAndTime] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState("");
  const [searchTerm1, setSearchTerm1] = useState("");
  const [searchValue1, setSearchValue1] = useState("");
  const [searchTerm2, setSearchTerm2] = useState("");
  const [searchValue2, setSearchValue2] = useState("");
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await fetchPkDetail(id);
        if (response && response.data) {
          setSearchValue1(response.data.data.opponentOneUser.code);
          setSearchTerm1(response.data.data.opponentOneUser.code);
          setSearchValue2(response.data.data.opponentTwoUser.code);
          setSearchTerm2(response.data.data.opponentTwoUser.code);
          setSelectedDateAndTime(dayjs(response.data.data.pkDate));
          setSelectedLocation(response.data.data.location);
          setPkName(response.data.data.name);
        } else {
          enqueueSnackbar(
            response?.error ||
              response?.message ||
              response?.data?.error ||
              response?.data?.message,
            { variant: "error" }
          );
        }
      } catch (error) {
        console.error(`Failed to fetch user details:`, error);
      } finally {
        setLoading(false);
      }
    };
    if (id) {
      fetchData();
    }
  }, [id]);
  const searchData = async (searchTerm, setData, setLoading) => {
    setLoading(true);
    try {
      const response = await searchUser({ codes: [searchTerm] });
      if (response && response.data) {
        setData(response.data.data[0] || null);
      }
    } catch (error) {
      console.error("Search failed:", error);
    } finally {
      setLoading(false);
    }
  };

  const debounceSearch1 = useCallback(
    debounce((query) => setSearchTerm1(query), 1000),
    []
  );

  const debounceSearch2 = useCallback(
    debounce((query) => setSearchTerm2(query), 1000),
    []
  );

  useEffect(() => {
    if (searchTerm1.length === 8) {
      searchData(searchTerm1, setData1, setSearchLoading1);
    }
  }, [searchTerm1]);

  useEffect(() => {
    if (searchTerm2.length === 8) {
      searchData(searchTerm2, setData2, setSearchLoading2);
    }
  }, [searchTerm2]);

  const handlePkNameChange = (e) => {
    setPkName(e.target.value);
  };

  const handleLocationChange = (e) => {
    setSelectedLocation(e.target.value);
  };

  const handleSendPk = async () => {
    if (!data1 || !data2) {
      setMessage(true);
      return;
    }

    if (searchTerm1 === searchTerm2) {
      setMessage(true);
      enqueueSnackbar("Opponents should not be equal", { variant: "error" });
      setData1(null);
      setData2(null);
      setSearchValue1("");
      setSearchValue2("");
      return;
    }

    if (!pkName) {
      setMessage(true);
      return;
    }

    if (!selectedDateAndTime) {
      setMessage(true);
      return;
    }

    if (!selectedLocation) {
      setMessage(true);
      return;
    }

    try {
      const payload = {
        name: pkName,
        opponentOne: data1.id,
        opponentTwo: data2.id,
        location: selectedLocation,
        pkDate: selectedDateAndTime,
      };
      let response;
      if (type === "Update") {
        response = await updatePk(id, payload);
      } else {
        response = await setPk(payload);
      }
      if (
        type == "Update"
          ? response?.data?.statusCode == 200
          : response.status == 201
      ) {
        enqueueSnackbar(
          type == "Update" ? response?.data?.message : "PK set successfully!",
          { variant: "success" }
        );
        navigate("/pk");
        setPkName("");
        setSelectedLocation("");
        setSelectedDateAndTime(null);
        setData1(null);
        setData2(null);
        setSearchTerm1("");
        setSearchTerm2("");
      } else {
        enqueueSnackbar(response?.message || response?.error, {
          variant: "error",
        });
      }
    } catch (error) {
      console.error("Error setting PK:", error);
      enqueueSnackbar("An error occurred while setting PK.", {
        variant: "error",
      });
    }
  };

  const labelStyles = {
    color: "", // Default label color
    "&.Mui-focused": {
      color: "#8F29D0", // Focused label color (green)
    },
  };
  const inputStyles = {
    marginBottom: 2,
    "& .MuiOutlinedInput-root": {
      "&:hover fieldset": {
        borderColor: "#8F29D0", // Hover border color
      },
      "&.Mui-focused fieldset": {
        borderColor: "#8F29D0", // Focus border color (green)
      },
    },
  };

  return (
    <div className="wallet-area">
      <div className="Wallet-form">
        <Typography variant="h5">
          <b>{type} PK Match</b>
        </Typography>
        <Box sx={{ marginBottom: 2 }}>
          <Box
            sx={{
              gap: 2,
              display: "flex",
              alignContent: "center",
              marginBottom: 2,
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                width: "50%",
              }}
            >
              {data1 && (
                <>
                  <Avatar
                    alt={data1.name}
                    src={
                      data1.picture?.startsWith("http")
                        ? data1.picture
                        : fileUrl + data1.picture
                    }
                    sx={{ height: "80px", width: "80px", margin: "10px" }}
                  />
                  <Typography variant="h6">{data1.name}</Typography>
                </>
              )}
            </Box>

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                position: "relative",
                height: "auto",
              }}
            >
              {/* Top Part of the Divider */}
              <Box
                sx={{
                  width: "2px",
                  height: "50%",
                  transform: "rotate(5deg)",
                  marginLeft: "5px",
                  background: gradientStyle,
                }}
              />

              {/* Centered Text */}
              <Typography
                variant="caption"
                sx={{
                  fontSize: "18px",
                  fontWeight: 600,
                  fontStyle: "italic",
                  lineHeight: 1,
                  margin: 0,
                  padding: 0,
                  display: "inline-block",
                  background: gradientStyle,
                  backgroundClip: "text",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                VS
              </Typography>

              {/* Bottom Part of the Divider */}
              <Box
                sx={{
                  width: "2px",
                  height: "50%",
                  marginLeft: "-10px",
                  transform: "rotate(5deg)",
                  background: gradientStyle,
                }}
              />
            </Box>

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                width: "50%",
              }}
            >
              {data2 && (
                <>
                  <Avatar
                    alt={data2.name}
                    src={
                      data2.picture?.startsWith("http")
                        ? data2.picture
                        : fileUrl + data2.picture
                    }
                    sx={{ height: "80px", width: "80px", margin: "10px" }}
                  />
                  <Typography variant="h6">{data2.name}</Typography>
                </>
              )}
            </Box>
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              marginBottom: 2,
              gap: 2,
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                width: "50%",
              }}
            >
              <TextField
                fullWidth
                label="Search User 1"
                variant="outlined"
                value={searchValue1}
                onChange={(e) => {
                  setSearchValue1(e.target.value);
                  debounceSearch1(e.target.value);
                }}
                disabled={searchLoading1}
                sx={inputStyles}
                InputLabelProps={{ sx: labelStyles }}
              />
              <FormHelperText sx={{ color: "red" }}>
                {message === true &&
                  (!data1
                    ? "User is Required"
                    : searchTerm1
                    ? "Use Different RID"
                    : null)}
              </FormHelperText>
            </Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                width: "50%",
              }}
            >
              <TextField
                fullWidth
                label="Search User 2"
                variant="outlined"
                value={searchValue2}
                onChange={(e) => {
                  setSearchValue2(e.target.value);
                  debounceSearch2(e.target.value);
                }}
                disabled={searchLoading2}
                sx={inputStyles}
                InputLabelProps={{ sx: labelStyles }}
              />
              <FormHelperText sx={{ color: "red" }}>
                {message === true &&
                  (!data2
                    ? "User is Required"
                    : searchTerm1
                    ? "Use Different RID"
                    : null)}
              </FormHelperText>
            </Box>
          </Box>
          <TextField
            fullWidth
            label="PK Name"
            variant="outlined"
            value={pkName}
            onChange={handlePkNameChange}
            sx={inputStyles}
            InputLabelProps={{ sx: labelStyles }}
          />
          <FormHelperText sx={{ color: "red" }}>
            {message == true && !pkName && "PK Name is Required"}
          </FormHelperText>

          <Box
            sx={{
              gap: 2,
              display: "flex",
              alignContent: "center",
              marginBottom: 2,
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                width: "50%",
              }}
            >
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DateTimePicker
                  label="Select Date & Time"
                  value={selectedDateAndTime}
                  onChange={(newValue) => setSelectedDateAndTime(newValue)}
                  minDate={dayjs()}
                  InputLabelProps={{ sx: labelStyles }}
                  sx={inputStyles}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      variant="outlined"
                      sx={inputStyles}
                      InputLabelProps={{ sx: labelStyles }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <CalendarTodayIcon
                              style={{
                                fontSize: "18px",
                                backgroundColor: "pink",
                              }}
                            />
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                />
              </LocalizationProvider>
              <FormHelperText sx={{ color: "red" }}>
                {message == true &&
                  !selectedDateAndTime &&
                  "Date & Time is Required"}
              </FormHelperText>
            </Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                width: "50%",
              }}
            >
              <TextField
                select
                fullWidth
                label="Select Location"
                variant="outlined"
                value={selectedLocation}
                onChange={handleLocationChange}
                sx={inputStyles}
                InputLabelProps={{ sx: labelStyles }}
              >
                <MenuItem value="Pakistan">Pakistan</MenuItem>
                <MenuItem value="Global">Global</MenuItem>
              </TextField>
              <FormHelperText sx={{ color: "red" }}>
                {message == true && !selectedLocation && "Location is Required"}
              </FormHelperText>
            </Box>
          </Box>
          <Button
            fullWidth
            variant="contained"
            onClick={handleSendPk}
            className="gradientButton"
            disabled={searchLoading1 || searchLoading2}
          >
            {type} PK
          </Button>
        </Box>
      </div>
    </div>
  );
};

export default SetPk;
